import { useEffect, useRef, useState } from 'react';

import {
    EPreApprovalVerdicts,
    fetchPreapprovalVerdict,
} from '@/apiCalls/fetchPreapprovalVerdict';
import { AnimationHelper } from '@/components/AnimationHelper';
import { DisplayComponent } from '@/components/DisplayComponent';
import { Header } from '@/components/Header';
import { Loading } from '@/components/Loading';
import {
    ELoanUsage,
    EUserFlow,
    selectLoanUsage,
    setIsPreapproved,
} from '@/context/borrower';
import {
    EDisplays,
    EJourneys,
    EModals,
    selectPartner,
    selectPreviousDisplay,
    selectUserFlow,
    setDisplay,
    setModal,
    setPreviousDisplay,
    setShowOcrDisplay,
} from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';
import { EParameters, getParameters } from '@/utils/setParametersFromParams';

export const baseId = 'screen-preApprovalAnimation';
export const testId = 'PreApprovalAnimation';

const delayExecution = (mls: number): Promise<unknown> => {
    return new Promise((resolve) => {
        setTimeout(() => resolve('ok'), mls);
    });
};

export function PreApprovalAnimation(): JSX.Element {
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const checking = useCopy().checkingApproval;
    const { showScoresScreen } = useCopy();
    const previousDisplay = useAppSelector(selectPreviousDisplay);
    const wasPreviousDisplayIndicativelyApproved =
        previousDisplay === EDisplays.INDICATIVELY_APPROVED;
    const partner = useAppSelector(selectPartner);
    const loanUsage = useAppSelector(selectLoanUsage);
    const { earlyReferredScreen, showQuickQuote } = partner;
    const userFlow = useAppSelector(selectUserFlow);
    const params = getParameters();

    const paramJourney = params.get(EParameters.INITIAL_JOURNEY);
    const isTestDrive =
        paramJourney?.toLocaleUpperCase() === EJourneys.TEST_DRIVE;
    // ***** Local State *****
    const placeholderText = [
        'CHECKING DETAILS',
        'CHECKING LOAN STRUCTURE',
        'CHECKING INCOME AND EXPENSES',
        'CHECKING IDENTIFICATION',
        'GRABBING CREDIT FILE INFO',
        'CONTACTING LENDER FOR DECISION',
    ];
    const [state, setState] = useState('');

    const changePlaceholder = async (): Promise<void> => {
        for (let i = 0; i < placeholderText?.length; i++) {
            await delayExecution(2000);
            setState(placeholderText[i]);
        }
    };

    // Add this ref to track if we've already made the API call
    const hasCalledApi = useRef(false);

    // ...existing code...

    useEffect(() => {
        // Skip if we've already made the call
        if (hasCalledApi.current) return;

        // Set flag to true immediately
        hasCalledApi.current = true;

        if (
            userFlow === EUserFlow.CAMPAIGN ||
            userFlow === EUserFlow.NO_LENDER ||
            userFlow === EUserFlow.NO_LENDER_EARLY_SIGNUP
        ) {
            fetchPreapprovalVerdict()
                .then(() => skipVerdict())
                .catch(() => skipVerdict());
        } else {
            loadVerdict();
            changePlaceholder();
        }
    }, []);

    function loadVerdict(): void {
        fetchPreapprovalVerdict(undefined, isTestDrive)
            .then((response) => {
                if (response.approvedLenders?.length) {
                    dispatch(setIsPreapproved(true));
                    proceedToNextScreen(response.verdict);
                } else {
                    dispatch(setIsPreapproved(false));
                    proceedToNextScreen(EPreApprovalVerdicts.REFERRED);
                }
            })
            .catch(() => {
                proceedToNextScreen(EPreApprovalVerdicts.REFERRED);
            });
    }

    async function proceedToNextScreen(
        verdict: EPreApprovalVerdicts
    ): Promise<void> {
        // Proceed to next screen
        const nextScreen = getNextScreen(verdict);
        dispatch(setDisplay(nextScreen));
        if (nextScreen === EDisplays.EMPLOYMENT) {
            dispatch(setModal(EModals.WORK_HISTORY));
        }

        dispatch(setShowOcrDisplay(false));
    }

    function skipVerdict(): void {
        const nextScreen = getNextScreenBasedOnLoanUsage();
        dispatch(setDisplay(nextScreen));
        if (nextScreen === EDisplays.EMPLOYMENT) {
            dispatch(setModal(EModals.WORK_HISTORY));
        }

        dispatch(setShowOcrDisplay(false));
    }

    function getNextScreenBasedOnLoanUsage(): EDisplays {
        if (loanUsage === ELoanUsage.BUSINESS) {
            return EDisplays.ASSETS_AND_LIABILITIES;
        } else {
            return EDisplays.EMPLOYMENT;
        }
    }

    function getNextScreen(
        verdict: EPreApprovalVerdicts | undefined
    ): EDisplays {
        switch (verdict) {
            case EPreApprovalVerdicts.APPROVED:
                if (wasPreviousDisplayIndicativelyApproved) {
                    dispatch(setPreviousDisplay(undefined));
                    return EDisplays.INDICATIVELY_APPROVED;
                } else {
                    if (
                        partner.userFlow !== EUserFlow.NO_LENDER &&
                        partner.userFlow !== EUserFlow.NO_LENDER_EARLY_SIGNUP &&
                        showQuickQuote
                    )
                        // Straight to the options when the scores screen is
                        // off: that is where its Continue button led, so
                        // skipping it must not drop the customer somewhere
                        // else in the journey.
                        return showScoresScreen
                            ? EDisplays.YOUR_SCORES
                            : EDisplays.INDICATIVELY_APPROVED;

                    if (loanUsage === ELoanUsage.BUSINESS) {
                        return EDisplays.ASSETS_AND_LIABILITIES;
                    }

                    return EDisplays.EMPLOYMENT;
                }
            case EPreApprovalVerdicts.DECLINED:
            case EPreApprovalVerdicts.REFERRED:
            case undefined:
            default:
                if (earlyReferredScreen) {
                    return EDisplays.INDICATIVELY_REFERRED;
                }
                if (loanUsage === ELoanUsage.BUSINESS) {
                    return EDisplays.ASSETS_AND_LIABILITIES;
                }

                return EDisplays.EMPLOYMENT;
        }
    }

    return (
        <div data-testid={testId}>
            {/* fillHeight: this screen has no button row, so its content is
                the last child — and the last child is normally pushed to the
                bottom and bottom-aligned for the buttons. On a short viewport
                that put the message against the bottom edge and clipped it. */}
            <DisplayComponent baseId={baseId} fillHeight>
                <Header />
                {checking.body.length ? (
                    // A partner can say what is happening once, rather than
                    // narrating each step of the check to the customer.
                    <div className='_flex flex-col items-center text-center pt-10 px-4'>
                        <Loading />
                        <h1 className='text-xl font-bold pt-6'>
                            {checking.title}
                        </h1>
                        {checking.body.map((line) => (
                            <p
                                key={line}
                                className='text-sm text-textBody pt-2'
                            >
                                {line}
                            </p>
                        ))}
                    </div>
                ) : (
                    <AnimationHelper array={placeholderText} />
                )}
            </DisplayComponent>
        </div>
    );
}
